import { floorSlotsFor, stackLayersFor } from "./calculate-fit";
import type { FloorSlot } from "./floor-layout";
import {
  isCylindrical,
  type ItemDimensions,
  type LoadingType,
  type TruckSpec,
} from "./types";

export interface AddedLoad {
  item: ItemDimensions;
  quantity: number;
}

const LOADING_TYPES: Array<LoadingType> = ["straight", "turned", "pinwheel"];

function slotDepthIn(item: ItemDimensions, slot: FloorSlot): number {
  if (isCylindrical(item)) return Math.max(item.lengthIn, item.widthIn);
  return slot.turned ? item.widthIn : item.lengthIn;
}

function spanOf(item: ItemDimensions, slots: Array<FloorSlot>): number {
  let frontIn = Number.POSITIVE_INFINITY;
  let backIn = 0;
  for (const slot of slots) {
    const halfDepthIn = slotDepthIn(item, slot) / 2;
    frontIn = Math.min(frontIn, slot.alongIn - halfDepthIn);
    backIn = Math.max(backIn, slot.alongIn + halfDepthIn);
  }
  return backIn - frontIn;
}

export function reservedLengthIn(truck: TruckSpec, load: AddedLoad): number {
  const { item, quantity } = load;
  if (item.lengthIn <= 0 || item.widthIn <= 0 || quantity <= 0) return 0;
  const layers = Math.max(stackLayersFor(truck, item), 1);
  const floorSpots = Math.ceil(quantity / layers);
  const loadingTypes = isCylindrical(item) ? ["straight" as const] : LOADING_TYPES;
  let shortestIn = truck.lengthIn;
  for (const loadingType of loadingTypes) {
    const slots = floorSlotsFor(truck, item, loadingType);
    if (slots.length < floorSpots) continue;
    shortestIn = Math.min(shortestIn, spanOf(item, slots.slice(0, floorSpots)));
  }
  return shortestIn;
}

export function reserveAddedLoads(
  truck: TruckSpec,
  loads: Array<AddedLoad>,
): TruckSpec {
  if (loads.length === 0) return truck;
  let lengthIn = truck.lengthIn;
  let weightLbs = truck.maxWeightLbs;
  for (const load of loads) {
    lengthIn -= reservedLengthIn({ ...truck, lengthIn: Math.max(lengthIn, 0) }, load);
    weightLbs -= Math.max(load.item.weightLbs, 0) * Math.max(load.quantity, 0);
  }
  return {
    ...truck,
    lengthIn: Math.max(lengthIn, 0),
    maxWeightLbs: Math.max(weightLbs, 0),
  };
}
